import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Heading, Box, Center, VStack} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import {MaskedInput} from '../../../../../components/inputs';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import useToast from '../../../../../hooks';
import {message as t} from '../../../../../utils/messages';
import {useContextState} from '../../../../../context/sub-client';
import {isWebApp} from '../../../../../utils/platform/is-web';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {StackParamList} from '../../../../../navigators';
import InternalTemplate from '../../../../../components/templates/internal';
import {SpinnerModal} from '../../../../../components/modals';
import {IC2PPaymentFormConfirmation} from '../../../../../interfaces/forms/c2p-payment-form-confirmation.interface';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'PaymentC2pConfirmationScreen'
>;

type IC2PPaymentFormConfirmationForm = Omit<
  IC2PPaymentFormConfirmation,
  'transactionId'
>;

const PaymentC2PConfirmationScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {sendPaymentC2PConfirmation, statePaymentC2P} = useContextState();
  const {showInputsError, showC2PError} = useToast();

  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      c2pKey: undefined,
    } as IC2PPaymentFormConfirmationForm,
  });

  const onInvalid: SubmitErrorHandler<IC2PPaymentFormConfirmationForm> = async (
    fieldErrors: FieldErrors<IC2PPaymentFormConfirmationForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleClearForm = () => {
    reset();
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const onSubmit: SubmitHandler<IC2PPaymentFormConfirmationForm> = async (
    form: IC2PPaymentFormConfirmationForm,
  ) => {
    try {
      setIsLoading(true);
      const result = await sendPaymentC2PConfirmation({
        c2pKey: form.c2pKey,
        transactionId: statePaymentC2P?.id,
      });
      if (result) {
        navigation.navigate('C2pPaymentSuccessScreen');
      }
      setIsLoading(false);
      return true;
    } catch {
      setIsLoading(false);
      showC2PError();
    }
    return false;
  };

  return (
    <InternalTemplate
      title={'screen-c2p-payment-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <VStack space={3} justifyContent="center" marginX={4}>
        <SpinnerModal
          show={isLoading}
          title={t('screen-c2p-payment-send-pay')}
        />
        <Box flex={1}>
          <KeyboardAwareScrollView
            style={styles.content}
            enableOnAndroid={true}
            scrollEnabled={true}
            enableAutomaticScroll={true}
            showsVerticalScrollIndicator={isWebApp}
            extraHeight={100}
            extraScrollHeight={100}
            innerRef={ref => {
              (this as any).scroll = ref;
            }}>
            <Center width="100%" flex={1} justifyContent="flex-start">
              <Box width="100%" py={5}>
                <Heading
                  size="xs"
                  paddingBottom="5"
                  fontWeight="bold"
                  color="#06355E">
                  {t('screen-c2p-payment-label')}
                </Heading>
                <MaskedInput
                  control={control}
                  name="c2pKey"
                  label="screen-c2p-payment-dynamic-key-label"
                  mask="c2p-key"
                  error={errors.c2pKey}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <Heading size="sm" paddingBottom="5" color="#06355E">
                  {statePaymentC2P ? statePaymentC2P.message : ''}
                </Heading>
                <Box mt={'5'}>
                  <PrimaryButton
                    label="screen-c2p-payment-send"
                    onPress={handleSubmit(onSubmit, onInvalid)}
                  />
                </Box>
                <Box mt={5} mb={200}>
                  <SecondaryButton
                    label="screen-c2p-payment-button-cancel"
                    onPress={handleClearForm}
                  />
                </Box>
              </Box>
            </Center>
          </KeyboardAwareScrollView>
        </Box>
      </VStack>
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  file: {
    marginTop: 15,
  },
});

export {PaymentC2PConfirmationScreen};
